import { Operation } from 'fast-json-patch';
import { useEffect, useRef } from 'react';
import { getAuthToken, validateOrRenewUser } from '../AppShell/Authentication/ApplicationAuthentication';
import { Nullable } from './Types';

export interface IGuidResult {
    id: string;
}

export interface IIdentifierDetails {
    id: string;
    authoringSystem: string;
    authoringSystemForeignKey: string;
    updatedOn: string;
    updatedBy: string;
}

export interface IUnprocessableEntity {
    code: string;
    message: string;
    content?: any;
}

export function createFormData<T>(data: T & { file?: File }): FormData {
    const fd = new FormData();
    Object.entries(data).forEach(([key, value]) => {
        if (key === 'file') {
            const file = value as File;
            fd.append('file', file, file.name);
        } else {
            if (typeof value === 'string') fd.append(key, value);
            else fd.append(key, JSON.stringify(value));
        }
    });

    return fd;
}

export type SendFileInterface = {
    progress(event: any): void;
    load(event: any, request: XMLHttpRequest): void;
    error(event: any, request: XMLHttpRequest): void;
};

async function appendCommonHeaders(headers?: Headers, isPatch = false): Promise<Headers> {
    if (!headers) headers = new Headers();

    headers.append('Content-Type', isPatch ? 'application/json-patch+json' : 'application/json');
    const barerToken = await getAuthToken();
    headers.append('Authorization', barerToken);

    // IE 11 aggressively caches API requests. Using 'no-cache' on all requests
    // forces IE 11 to behave as-expected at the cost of performance for all
    // browsers
    headers.append('Cache-Control', 'no-cache');
    headers.append('Pragma', 'no-cache');

    return headers;
}

function logResponse(method: string, route: string, response: Response): void {
    if (response.status === 422) {
        console.warn(`[${method}] ${response.status} ${route}`);
    } else if (response.status >= 400 && response.status !== 422) {
        console.error(`[${method}] ${response.status} ${route}`);
    }
}

function processResponse<TResult>(response: Response): Promise<any> {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.match(/^application\/json/)) {
        return response.json() as Promise<TResult>;
    }

    if (contentType.match(/^(text\/plain|image\/.*|audio\/.*|video\/.*|application\/.*)/)) {
        return response.blob();
    }

    return response.text();
}

export class HttpRequestExecutor {
    constructor(public baseUrl: string) {}

    public async get<TResult>(route: string): Promise<TResult> {
        const dataObject = {
            method: 'GET'
        };

        const url = `${this.baseUrl}${route}`;

        return this.doRequest(url, route, dataObject);
    }

    public async getCancellable<TResult>(
        route: string,
        config: HttpExecutorConfig
    ): Promise<TResult | CancelledRequest> {
        const signal = config.abortAndGetNewSignal();
        const dataObject = {
            method: 'GET',
            signal
        };

        const url = `${this.baseUrl}${route}`;

        return this.doCancellableRequest(url, route, dataObject);
    }

    public async put<TResult>(route: string): Promise<TResult>;
    public async put<TData, TResult>(route: string, data: TData): Promise<TResult>;
    public async put<TData = any, TResult = any>(route: string, data?: TData): Promise<TResult> {
        const dataObject: {
            method: string;
            body?: string;
        } = {
            method: 'PUT'
        };

        if (data) {
            dataObject.body = JSON.stringify(data || '');
        }

        const url = `${this.baseUrl}${route}`;

        return this.doRequest(url, route, dataObject);
    }

    public async putCancellable<TResult>(
        route: string,
        config: HttpExecutorConfig
    ): Promise<TResult | CancelledRequest>;
    public async putCancellable<TData, TResult>(
        route: string,
        config: HttpExecutorConfig,
        data: TData
    ): Promise<TResult | CancelledRequest>;
    public async putCancellable<TData = any, TResult = any>(
        route: string,
        config: HttpExecutorConfig,
        data?: TData
    ): Promise<TResult | CancelledRequest> {
        const signal = config.abortAndGetNewSignal();

        const dataObject: {
            method: string;
            body?: string;
            signal: AbortSignal;
        } = {
            method: 'PUT',
            signal
        };

        if (data) {
            dataObject.body = JSON.stringify(data || '');
        }

        const url = `${this.baseUrl}${route}`;

        return this.doCancellableRequest(url, route, dataObject);
    }

    public async patch<TResult>(route: string, data?: Operation[], signal?: AbortSignal): Promise<TResult> {
        const dataObject: {
            method: string;
            body?: string;
            signal?: AbortSignal;
        } = {
            method: 'PATCH',
            signal
        };

        if (data) {
            dataObject.body = JSON.stringify(data || '');
        }

        const url = `${this.baseUrl}${route}`;

        return this.doRequest(url, route, dataObject, true);
    }

    public async post<TData, TResult>(route: string, data?: TData, signal?: AbortSignal): Promise<TResult> {
        const dataObject = {
            method: 'POST',
            body: JSON.stringify(data),
            signal
        };

        const url = `${this.baseUrl}${route}`;

        return this.doRequest(url, route, dataObject);
    }

    public async postCancellable<TData, TResult>(
        route: string,
        config: HttpExecutorConfig,
        data?: TData
    ): Promise<TResult | CancelledRequest> {
        const signal = config.abortAndGetNewSignal();

        const dataObject = {
            method: 'POST',
            body: JSON.stringify(data),
            signal
        };

        const url = `${this.baseUrl}${route}`;

        return this.doCancellableRequest(url, route, dataObject);
    }

    public async sendFile(route: string, formData: FormData, config: SendFileInterface): Promise<void> {
        await validateOrRenewUser();

        const url = `${this.baseUrl}${route}`;

        const req = new XMLHttpRequest();

        req.open('POST', url);
        req.setRequestHeader('Authorization', await getAuthToken());

        req.upload.addEventListener('progress', config.progress);

        // Using addEventListener doesn't properly work, just use these call backs.
        req.onload = event => config.load(event, req);
        req.onerror = event => config.error(event, req);

        req.send(formData);
    }

    public async delete(route: string): Promise<Response> {
        const dataObject = {
            method: 'DELETE'
        };

        const url = `${this.baseUrl}${route}`;

        return this.doRequest(url, route, dataObject);
    }

    private async doRequest<TResult>(
        url: string,
        route: string,
        dataObject: {
            method: string;
            body?: BodyInit;
            headers?: Headers;
            signal?: AbortSignal;
        },
        isPatch?: boolean
    ): Promise<TResult> {
        await validateOrRenewUser();
        dataObject.headers = await appendCommonHeaders(dataObject.headers, isPatch);

        try {
            const response = await fetch(url, dataObject);
            logResponse(dataObject.method, route, response);
            if (response.status >= 400 && response.status < 600) {
                throw response;
            } else {
                return processResponse<TResult>(response);
            }
        } catch (err) {
            if (err instanceof Response && err.status && err.status === 422) {
                throw (await err.json()) as Promise<IUnprocessableEntity>;
            } else {
                throw err;
            }
        }
    }

    private async doCancellableRequest<TResult>(
        url: string,
        route: string,
        dataObject: {
            method: string;
            body?: BodyInit;
            headers?: Headers;
            signal: AbortSignal;
        },
        isPatch?: boolean
    ): Promise<TResult | CancelledRequest> {
        await validateOrRenewUser();
        dataObject.headers = await appendCommonHeaders(dataObject.headers, isPatch);

        try {
            const response = await fetch(url, dataObject);
            logResponse(dataObject.method, route, response);
            if (response.status >= 400 && response.status < 600) {
                throw response;
            } else {
                return processResponse<TResult>(response);
            }
        } catch (err) {
            if (err instanceof Response && err.status && err.status === 422) {
                throw (await err.json()) as Promise<IUnprocessableEntity>;
            }

            if (err instanceof Error && err.name === 'AbortError') {
                return new CancelledRequest();
            }

            throw err;
        }
    }
}

export class CancelledRequest {}

export type HttpExecutorConfig = {
    cleanUp(): void;
    abortAndGetNewSignal(): AbortSignal;
};

// Used exclusively for class based solutions.
// ie support for AsyncFetch.
export class HttpExecutorConfigController implements HttpExecutorConfig {
    private controller: Nullable<AbortController> = null;

    abortAndGetNewSignal = (): AbortSignal => {
        if (this.controller) {
            this.controller.abort();
        }

        const newController = new AbortController();
        this.controller = newController;

        return newController.signal;
    };

    cleanUp = (): void => {
        if (this.controller) {
            this.controller.abort();
        }
    };
}

// Used in some PoC areas of the application
export class MockController implements HttpExecutorConfig {
    abortAndGetNewSignal = (): AbortSignal => {
        const newController = new AbortController();
        return newController.signal;
    };
    cleanUp = (): void => undefined;
}

export const useHttpConfig = (): HttpExecutorConfig => {
    const lastAbortController = useRef<Nullable<AbortController>>(null);

    const abortAndGetNewSignal = (): AbortSignal => {
        if (lastAbortController.current) {
            lastAbortController.current.abort();
        }

        const newController = new AbortController();
        lastAbortController.current = newController;

        return newController.signal;
    };

    const cleanUp = () => {
        if (lastAbortController.current) {
            lastAbortController.current.abort();
        }
    };

    // Ensure any requests currently controlled by this are aborted
    // when the hook unmounts
    useEffect(() => {
        return () => {
            cleanUp();
        };
    }, []);

    return {
        abortAndGetNewSignal,
        cleanUp
    };
};

export function isHttpResponse(item: unknown): item is Response {
    return (item as Response).status !== undefined;
}

export function isHttpAuthenticationError(item: unknown): boolean {
    if (isHttpResponse(item)) {
        if (item.status === 401 || item.status === 403) return true;
    }

    return false;
}

export const getExecutor = (url: string): HttpRequestExecutor => {
    return new HttpRequestExecutor(url);
};

export type HttpQuery<TResponse> = (
    config: HttpExecutorConfig,
    ...args: any[]
) => Promise<TResponse | CancelledRequest>;
